/**
 * Seed Zwaanswyk School Offences
 * Creates thematic discipline categories and all offences from the school code of conduct.
 * Each offence has a level (1-4 severity) AND a categoryId (thematic grouping).
 *
 * USAGE: await window.seedZwaanswykOffences()
 */

import { offenceService, categoryService } from '../services/disciplineService';

const getSchoolId = () => {
  const key = Object.keys(localStorage).find(k => k.startsWith('swan_school_context_'));
  if (!key) return null;
  try {
    return JSON.parse(localStorage.getItem(key)).schoolId || null;
  } catch { return null; }
};

// Thematic categories (NOT severity levels — those are the `level` field on offences)
const CATEGORIES = [
  { key: 'academic',    name: 'Academic',           description: 'Schoolwork, homework, cheating, materials',          color: '#3B82F6' },
  { key: 'conduct',     name: 'Conduct',            description: 'Behaviour, disruption, disrespect, bullying',       color: '#8B5CF6' },
  { key: 'appearance',  name: 'Appearance',         description: 'Uniform, hair, dress code, accessories',            color: '#EC4899' },
  { key: 'attendance',  name: 'Attendance',         description: 'Lateness, truancy, absenteeism',                    color: '#F59E0B' },
  { key: 'property',    name: 'Property',           description: 'Vandalism, theft, damage to property',              color: '#6366F1' },
  { key: 'substances',  name: 'Substance Abuse',    description: 'Drugs, alcohol, vaping, smoking',                   color: '#EF4444' },
  { key: 'sexual',      name: 'Sexual Misconduct',  description: 'Sexual behaviour, harassment, pornography',         color: '#DC2626' },
  { key: 'safety',      name: 'Safety & Violence',  description: 'Weapons, assault, fighting, fire crackers',         color: '#991B1B' },
  { key: 'extramural',  name: 'Extra-Mural',        description: 'Sports, activities, fixtures',                      color: '#14B8A6' }
];

// All offences mapped to thematic category + severity level
// ZT = zero tolerance (marked with * in the school document)
const OFFENCES = [
  // === ACADEMIC ===
  { cat: 'academic', level: 1, name: 'Failure to do classwork or submit homework' },
  { cat: 'academic', level: 1, name: 'Failure to bring required materials to lesson' },
  { cat: 'academic', level: 1, name: 'Failure to hand work in on time', zeroTolerance: true },
  { cat: 'academic', level: 1, name: 'Copying another learner\'s classwork or homework', zeroTolerance: true },
  { cat: 'academic', level: 1, name: 'Failure to return library book by due date' },
  { cat: 'academic', level: 2, name: 'Cheating in tests or exams' },
  { cat: 'academic', level: 2, name: 'Copying computer exercises, projects or year mark work' },

  // === CONDUCT ===
  { cat: 'conduct', level: 1, name: 'Excessively noisy or unruly behaviour' },
  { cat: 'conduct', level: 1, name: 'Eating or drinking during contact time', zeroTolerance: true },
  { cat: 'conduct', level: 1, name: 'Chewing gum', zeroTolerance: true },
  { cat: 'conduct', level: 1, name: 'Misconduct in assembly', zeroTolerance: true },
  { cat: 'conduct', level: 1, name: 'Entering out of bounds area without permission', zeroTolerance: true },
  { cat: 'conduct', level: 1, name: 'Loitering in passages, tuck shop, toilets or change rooms' },
  { cat: 'conduct', level: 1, name: 'Disruptive behaviour in class' },
  { cat: 'conduct', level: 1, name: 'Spitting' },
  { cat: 'conduct', level: 1, name: 'Littering' },
  { cat: 'conduct', level: 1, name: 'Continual interference with another learner causing minor discomfort', zeroTolerance: true },
  { cat: 'conduct', level: 1, name: 'Possession or use of cell phone or electronic devices', zeroTolerance: true },
  { cat: 'conduct', level: 2, name: 'Misconduct during detention' },
  { cat: 'conduct', level: 2, name: 'Bad attitude towards teacher including verbal insults' },
  { cat: 'conduct', level: 2, name: 'Not responding to educator instruction regarding disciplinary incident' },
  { cat: 'conduct', level: 2, name: 'Swearing, lying or obscene gestures', zeroTolerance: true },
  { cat: 'conduct', level: 2, name: 'Verbal or non-verbal abuse' },
  { cat: 'conduct', level: 2, name: 'Disrespect or insolence' },
  { cat: 'conduct', level: 2, name: 'Insubordination - failing to carry out instruction', zeroTolerance: true },
  { cat: 'conduct', level: 2, name: 'Bullying (verbal, physical, emotional or cyber)', zeroTolerance: true },
  { cat: 'conduct', level: 2, name: 'Public disturbance and public indecency' },
  { cat: 'conduct', level: 2, name: 'Bringing the school\'s name into disrepute' },
  { cat: 'conduct', level: 2, name: 'Possession of offensive material (excluding pornographic)' },
  { cat: 'conduct', level: 2, name: 'Racism or discrimination remarks/insults', zeroTolerance: true },
  { cat: 'conduct', level: 2, name: 'Forgery of documents including medical certificates', zeroTolerance: true },
  { cat: 'conduct', level: 2, name: 'Unreasonable repetition of a Level 1 offence' },
  { cat: 'conduct', level: 3, name: 'Conduct seriously detrimental to school order or discipline' },
  { cat: 'conduct', level: 3, name: 'Violating rights of learners to receive education' },
  { cat: 'conduct', level: 3, name: 'Violating rights of teachers to carry out duties' },
  { cat: 'conduct', level: 3, name: 'Illegal strike action or campaign on school premises' },
  { cat: 'conduct', level: 3, name: 'Unreasonable repetition of a Level 2 offence' },
  { cat: 'conduct', level: 4, name: 'Sedition or inciting illegal strike action on school premises' },
  { cat: 'conduct', level: 4, name: 'Any offence punishable under common law' },

  // === APPEARANCE ===
  { cat: 'appearance', level: 1, name: 'Uniform regulation infringements' },
  { cat: 'appearance', level: 1, name: 'Failure to wear correct school uniform in public' },
  { cat: 'appearance', level: 1, name: 'Failure to wear correct sports kit' },
  { cat: 'appearance', level: 2, name: 'Unacceptable hairstyles including bleaching or colouring' },

  // === ATTENDANCE ===
  { cat: 'attendance', level: 1, name: 'Failure to submit absentee note by deadline' },
  { cat: 'attendance', level: 1, name: 'Failure to attend compulsory activity without written explanation', zeroTolerance: true },
  { cat: 'attendance', level: 1, name: 'Reporting late for class' },
  { cat: 'attendance', level: 1, name: 'Arriving late for school without explanation' },
  { cat: 'attendance', level: 2, name: 'Failure to attend detention without written explanation' },
  { cat: 'attendance', level: 2, name: 'Truancy from contact time', zeroTolerance: true },
  { cat: 'attendance', level: 3, name: 'Truancy from school or leaving grounds without permission' },

  // === PROPERTY ===
  { cat: 'property', level: 1, name: 'Defacing school property', zeroTolerance: true },
  { cat: 'property', level: 2, name: 'Vandalism', zeroTolerance: true },
  { cat: 'property', level: 2, name: 'Interfering with another person\'s possessions without consent' },
  { cat: 'property', level: 2, name: 'Damaging another person\'s possessions without consent', zeroTolerance: true },
  { cat: 'property', level: 2, name: 'Tampering with safety equipment on school premises' },
  { cat: 'property', level: 4, name: 'Theft, robbery, breaking and entering' },
  { cat: 'property', level: 4, name: 'Malicious damage to property' },

  // === SUBSTANCE ABUSE ===
  { cat: 'substances', level: 3, name: 'Under the influence of alcohol or drugs at school' },
  { cat: 'substances', level: 3, name: 'Vaping - possession or use of vape/e-cigarette devices' },
  { cat: 'substances', level: 3, name: 'Possession or use of CBD products at school' },
  { cat: 'substances', level: 4, name: 'Possession, use or dealing in drugs or alcohol' },
  { cat: 'substances', level: 4, name: 'Dealing or distributing vapes/e-cigarettes at school' },

  // === SEXUAL MISCONDUCT ===
  { cat: 'sexual', level: 3, name: 'Sexually explicit comments, suggestions or notes' },
  { cat: 'sexual', level: 3, name: 'Sexual behaviour of a serious nature or coercion' },
  { cat: 'sexual', level: 3, name: 'Possession, distribution or display of pornographic material' },
  { cat: 'sexual', level: 4, name: 'Rape, attempted rape or indecent assault' },

  // === SAFETY & VIOLENCE ===
  { cat: 'safety', level: 2, name: 'Fighting, common assault or attempted assault' },
  { cat: 'safety', level: 2, name: 'Possession or use of fire crackers', zeroTolerance: true },
  { cat: 'safety', level: 3, name: 'Possession of weapons (knives, etc.)' },
  { cat: 'safety', level: 3, name: 'Assault with intent to do grievous bodily harm' },
  { cat: 'safety', level: 4, name: 'Use of weapons causing physical injury' },
  { cat: 'safety', level: 4, name: 'Possession of firearm, ammunition or lethal weapon' },
  { cat: 'safety', level: 4, name: 'Poisoning or attempted poisoning' },
  { cat: 'safety', level: 4, name: 'Physical assault resulting in bodily harm' },

  // === EXTRA-MURAL ===
  { cat: 'extramural', level: 1, name: 'Misconduct during extra-mural practice' },
  { cat: 'extramural', level: 1, name: 'Failure to attend extra-mural practice without explanation' },
  { cat: 'extramural', level: 2, name: 'Misconduct during intra- or inter-school competition' },
  { cat: 'extramural', level: 2, name: 'Failure to attend extra-mural fixture as participant or official' }
];

async function seedZwaanswykOffences() {
  const schoolId = getSchoolId();
  if (!schoolId) {
    console.error('❌ No school context. Log in and select a school first.');
    return;
  }

  console.log(`🏫 Seeding Zwaanswyk offences for school: ${schoolId}`);
  console.log(`📁 ${CATEGORIES.length} categories, ${OFFENCES.length} offences\n`);

  // Step 1: Create categories
  const categoryMap = {}; // key → categoryId
  let catCreated = 0;
  let catSkipped = 0;

  const existingCategories = await categoryService.getCategories(schoolId);

  for (const cat of CATEGORIES) {
    try {
      const match = existingCategories.find(c => c.name === cat.name);
      if (match) {
        console.log(`⏭️  Category exists: ${cat.name} (${match.id})`);
        categoryMap[cat.key] = match.id;
        catSkipped++;
        continue;
      }

      const created = await categoryService.createCategory({
        name: cat.name,
        description: cat.description,
        color: cat.color,
        schoolId
      });
      categoryMap[cat.key] = created.id;
      catCreated++;
      console.log(`✅ Category: ${cat.name} → ${created.id}`);
    } catch (err) {
      console.error(`❌ Category failed: ${cat.name}`, err.message);
    }
  }

  console.log(`\n📁 Categories: ${catCreated} created, ${catSkipped} existing\n`);

  // Step 2: Create offences
  let offCreated = 0;
  let offSkipped = 0;
  let offFailed = 0;

  for (const off of OFFENCES) {
    const categoryId = categoryMap[off.cat];
    if (!categoryId) {
      console.error(`❌ No category for "${off.cat}", skipping: ${off.name}`);
      offFailed++;
      continue;
    }

    try {
      const existing = await offenceService.getOffenceByName(off.name, schoolId);
      if (existing) {
        console.log(`⏭️  ${off.name}`);
        offSkipped++;
        continue;
      }

      await offenceService.createOffence({
        name: off.name,
        level: off.level,
        categoryId,
        zeroTolerance: off.zeroTolerance || false,
        active: true,
        schoolId
      });
      offCreated++;
      console.log(`✅ L${off.level} ${off.name}`);
    } catch (err) {
      offFailed++;
      console.error(`❌ ${off.name}: ${err.message}`);
    }
  }

  console.log(`\n========================================`);
  console.log(`🏁 Done!`);
  console.log(`   Categories: ${catCreated} created, ${catSkipped} existing`);
  console.log(`   Offences:   ${offCreated} created, ${offSkipped} existing, ${offFailed} failed`);
  console.log(`========================================`);

  return { catCreated, catSkipped, offCreated, offSkipped, offFailed };
}

export { seedZwaanswykOffences };
